
import { useState } from 'react';
import { usePageTracking } from '../../../hooks/usePageTracking';

interface InsightNavigatorProps {
  onTooltipShow: (title: string, content: string) => void;
}

export default function InsightNavigator({ onTooltipShow }: InsightNavigatorProps) {
  const [activeTab, setActiveTab] = useState('onpage');
  usePageTracking({
    moduleName: 'SEO Manager',
    submoduleName: `Insight Navigator / ${activeTab}`,
    routePath: `/dashboard#insight-navigator/${activeTab}`,
  });

  const onPageIssues = [
    { type: 'critical', title: 'Missing Meta Descriptions', count: 12, impact: 'High' },
    { type: 'warning', title: 'Duplicate Title Tags', count: 8, impact: 'Medium' },
    { type: 'info', title: 'Long Title Tags', count: 15, impact: 'Low' },
    { type: 'critical', title: 'Missing H1 Tags', count: 5, impact: 'High' },
    { type: 'warning', title: 'Broken Internal Links', count: 23, impact: 'Medium' }
  ];

  const offPageMetrics = [
    { metric: 'Domain Authority', value: 45, target: 60, trend: 'up' },
    { metric: 'Backlink Quality Score', value: 78, target: 85, trend: 'up' },
    { metric: 'Referring Domains', value: 234, target: 300, trend: 'stable' },
    { metric: 'Brand Mentions', value: 89, target: 120, trend: 'up' }
  ];

  const coreWebVitals = [
    { metric: 'Largest Contentful Paint', value: '2.1s', status: 'good', target: '<2.5s' },
    { metric: 'First Input Delay', value: '45ms', status: 'good', target: '<100ms' },
    { metric: 'Cumulative Layout Shift', value: '0.08', status: 'needs-improvement', target: '<0.1' },
    { metric: 'First Contentful Paint', value: '1.2s', status: 'good', target: '<1.8s' }
  ];

  const getIssueIcon = (type: string) => {
    switch (type) {
      case 'critical': return 'ri-error-warning-line text-red-500';
      case 'warning': return 'ri-alert-line text-yellow-500';
      default: return 'ri-information-line text-blue-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return 'text-green-500';
      case 'needs-improvement': return 'text-yellow-500';
      case 'poor': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">SEO Insight Navigator</h2>
          <button
            onClick={() => onTooltipShow('Insight Navigator', 'Comprehensive analysis of your website\'s SEO health across technical, content, and performance factors')}
            className="text-gray-400 hover:text-blue-500"
          >
            <i className="ri-information-line text-xl"></i>
          </button>
        </div>

        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          {[
            { id: 'onpage', label: 'On-Page SEO', icon: 'ri-file-text-line' },
            { id: 'offpage', label: 'Off-Page SEO', icon: 'ri-links-line' },
            { id: 'vitals', label: 'Core Web Vitals', icon: 'ri-speed-line' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <i className={`${tab.icon} text-lg`}></i>
              <span className="font-medium whitespace-nowrap">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'onpage' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Issues List */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">On-Page Issues</h3>
            <div className="space-y-4">
              {onPageIssues.map((issue, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <i className={getIssueIcon(issue.type)}></i>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{issue.title}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Impact: {issue.impact}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-gray-900 dark:text-white">{issue.count}</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">pages affected</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Optimization Recommendations */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Priority Recommendations</h3>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <i className="ri-error-warning-line text-red-500"></i>
                  <span className="font-medium text-red-800 dark:text-red-400">Critical Priority</span>
                </div>
                <p className="text-sm text-red-700 dark:text-red-300">Fix missing meta descriptions on 12 pages to improve click-through rates</p>
              </div>
              
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <i className="ri-alert-line text-yellow-500"></i>
                  <span className="font-medium text-yellow-800 dark:text-yellow-400">Medium Priority</span>
                </div>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">Resolve duplicate title tags to avoid content confusion</p>
              </div>
              
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <i className="ri-information-line text-blue-500"></i>
                  <span className="font-medium text-blue-800 dark:text-blue-400">Low Priority</span>
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-300">Optimize title tag lengths for better search result display</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'offpage' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Authority Metrics */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Authority Metrics</h3>
            <div className="space-y-6">
              {offPageMetrics.map((metric, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{metric.metric}</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{metric.value}</span>
                      <i className={`text-sm ${
                        metric.trend === 'up' ? 'ri-arrow-up-line text-green-500' :
                        metric.trend === 'down' ? 'ri-arrow-down-line text-red-500' :
                        'ri-subtract-line text-gray-400'
                      }`}></i>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                      style={{ width: `${(metric.value / metric.target) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>Current: {metric.value}</span>
                    <span>Target: {metric.target}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Backlink Analysis */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Backlinks</h3>
            <div className="space-y-4">
              {[
                { domain: 'techcrunch.com', authority: 92, type: 'Editorial', date: '2 days ago' },
                { domain: 'marketingland.com', authority: 78, type: 'Guest Post', date: '5 days ago' },
                { domain: 'searchengineland.com', authority: 85, type: 'Mention', date: '1 week ago' },
                { domain: 'moz.com', authority: 91, type: 'Resource', date: '2 weeks ago' }
              ].map((link, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{link.domain}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{link.type} • {link.date}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-blue-600">DA {link.authority}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'vitals' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Core Web Vitals */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Core Web Vitals</h3>
            <div className="space-y-4">
              {coreWebVitals.map((vital, index) => (
                <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900 dark:text-white">{vital.metric}</span>
                    <span className={`font-bold ${getStatusColor(vital.status)}`}>{vital.value}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>Target: {vital.target}</span>
                    <span className={`capitalize ${getStatusColor(vital.status)}`}>{vital.status.replace('-', ' ')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Performance Trend</h3>
            <div className="h-64 bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-lg flex items-center justify-center">
              <img 
                src="https://readdy.ai/api/search-image?query=web%20performance%20metrics%20dashboard%20showing%20core%20web%20vitals%20scores%20with%20green%20and%20blue%20gradient%20colors%2C%20clean%20modern%20interface%20design%2C%20loading%20speed%20analytics%20chart&width=400&height=250&seq=2&orientation=landscape"
                alt="Core Web Vitals performance chart"
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
